<template>
  <div class="stickers">
    <div class="ms-container-header">
      <icon size="16" type="md-bookmarks" />
      Стикеры
    </div>
    <div class="stickers__add-row">
      <div class="stickers__filter">
        <i-switch v-model="allStickers" size="small" />
        <span class="ms-text">
          Показать выполненные
        </span>
      </div>
      <i-button type="primary" size="small" @click="editModal = true">
        Добавить стикер
      </i-button>
    </div>
    <card
      dis-hover
      :padding="0"
      class="stickers__container"
    >
      <div class="stickers__list" v-if="filteredStickers.length">
        <div
          class="stickers__item"
          v-for="sticker in filteredStickers"
          :key="sticker.id"
          :style="{ backgroundColor: getStickerColor(sticker.color) }"
        >
          <div class="stickers__item-checkbox">
            <checkbox
              @on-change="changeStatus(sticker)"
              v-model="sticker.completed"
            />
          </div>
          <div class="stickers__item-name" @click="openModal(sticker)">
            <div :class="sticker.completed ? 'ms-help' : 'ms-small-head'">
              {{ sticker.name }}
            </div>
            <div
              style="margin-top: -16px; white-space: pre-line"
              :class="sticker.completed ? 'ms-help' : 'ms-text'"
            >
              {{ sticker.description }}
            </div>
          </div>
          <div class="stickers__item-subinfo">
            <task-date
              class="sticker-date"
              :date="sticker.end_time"
              type="datetime"
              :status="{ id: 1, completed: sticker.completed }"
            />
            <div
              class="my-stickers__item-performer ms-help"
              v-if="sticker.assigned_to"
            >
              {{ getUserName(sticker) }}
            </div>
          </div>
        </div>
      </div>
      <div v-else class="ms-nodata ms-help">
        Активные стикеры отсутствуют. Попробуйте добавить новый стикер :)
      </div>
    </card>
    <modal
      :class="`modal-window ${theme}`"
      @on-cancel="closeModal"
      v-model="editModal"
      width="460"
      :styles="{top: '80px'}"
    >
      <p slot="header" style="color:#4484ce;text-align:center">
        <icon type="md-create" />
        <span style="margin-left: 8px;">
          {{ stickerId ? "Редактирование" : "Создание" }} стикера
        </span>
      </p>
      <div class="stickers__modal-content">
        <div class="stickers__modal-row name">
          <i-input
            class="name-input"
            placeholder="Название"
            v-model="stickerName"
          />
          <color-picker
            :transfer="false"
            :colors="colorPresets"
            v-model="stickerColor"
            recommend
          />
        </div>
        <div class="stickers__modal-row">
          <div class="ms-help label">
            Дополнительное описание
          </div>
          <i-input
            class="description-input"
            placeholder="Описание задачи"
            v-model="stickerDescription"
            type="textarea"
          />
        </div>
        <div class="stickers__modal-row dates">
          <div class="date-cell">
            <div class="ms-help label">
              Дата окончания
            </div>
            <date-picker
              v-model="stickerDateTime"
              :transfer="false"
              type="datetime"
              placeholder="Выберите дату и время"
              format="dd.MM.yyyy HH:mm"
            />
          </div>
          <div class="date-cell">
            <div class="ms-help label">
              Время уведомления
            </div>
            <i-select :transfer="false" v-model="notificationTime" size="small">
              <i-option
                v-for="item in notifications"
                :value="item.id"
                :key="item.id"
              >
                {{ item.name }}
              </i-option>
            </i-select>
          </div>
        </div>
        <div class="stickers__modal-row">
          <div class="ms-help label">
            Назначить на
          </div>
          <i-select
            :transfer="false"
            v-model="stickerPerformer"
            size="small"
            clearable
            filterable
            not-found-text="Не найдено"
          >
            <i-option v-for="item in users" :value="item.id" :key="item.id">{{
              item.id === "all" ? item.name : getFullName(item)
            }}</i-option>
          </i-select>
        </div>
      </div>
      <div class="stickers__modal-controls" slot="footer">
        <i-button
          :loading="deleting"
          type="error"
          size="small"
          @click="removeSticker"
          :disabled="isDeletingDisabled"
        >
          Удалить
        </i-button>
        <i-button
          :loading="editing"
          type="success"
          size="small"
          @click="saveSticker"
          :disabled="isCreatingDisabled"
        >
          Сохранить
        </i-button>
      </div>
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import TaskDate from '@/components/task/task-date'
import { hexToRgbA, colorPresets, getFullName } from '@/utils'

const notifications = [
  { id: '00:30:00', name: 'За 30 минут' },
  { id: '01:00:00', name: 'За 1 час' },
  { id: '03:00:00', name: 'За 3 часа' },
  { id: '24:00:00', name: 'За 1 день' }
]

export default {
  props: ['stickers'],
  components: {
    TaskDate
  },
  data () {
    return {
      editModal: false,
      editing: false,
      deleting: false,
      stickerId: '',
      stickerName: '',
      stickerColor: '',
      stickerDescription: '',
      stickerDateTime: new Date(),
      stickerPerformer: '',
      notificationTime: '00:30:00',
      allStickers: false,
      colorPresets,
      notifications
    }
  },
  computed: {
    userId () {
      const user = this.$store.state.auth.user || {}

      return user.id
    },
    theme () {
      return this.$store.state.theme
    },
    filteredStickers () {
      if (this.allStickers) return this.stickers

      return this.stickers.filter(s => !s.completed)
    },
    isCreatingDisabled () {
      return !this.stickerName
    },
    isDeletingDisabled () {
      return !this.stickerId
    },
    users () {
      return this.$store.state.users.filter(u => {
        const user = this.$store.state.auth.user || {}
        const { id } = user

        return u.id !== id
      })
    }
  },
  methods: {
    getUserName ({ assigned_to, user }) {
      if (user.id !== this.userId) {
        return `От: ${this.getFullName(user)}`
      }

      return `Исп.: ${this.getFullName(assigned_to)}`
    },
    getStickerColor (color) {
      const emptyColorBg =
        this.theme === 'dark' ? 'rgb(53, 66, 86)' : 'rgba(255,255,255,0.1)'

      return color ? hexToRgbA(color, this.theme) : emptyColorBg
    },
    getData () {
      return {
        id: this.stickerId || undefined,
        name: this.stickerName,
        description: this.stickerDescription,
        end_time: moment(this.stickerDateTime, 'DD.MM.YYYY HH:mm').format(),
        color: this.stickerColor,
        assigned_to_id: this.stickerPerformer || null,
        end_time_notify_lag: this.notificationTime
      }
    },
    async saveSticker () {
      try {
        this.editing = true
        const url = `page/stickers`
        const data = this.getData()
        await this.$store.dispatch('POST_REQUEST', { url, data })
        this.closeModal()
        this.editing = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Не удалось выполнить сохранение')
        this.editing = false
      }
    },
    async changeStatus ({ id, completed }) {
      try {
        this.editing = true
        const url = `page/stickers`
        const data = { id, completed }
        await this.$store.dispatch('POST_REQUEST', { url, data })
        this.editing = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Не удалось выполнить изменение статуса')
        this.editing = false
      }
    },
    async removeSticker () {
      try {
        this.deleting = true
        const url = `page/stickers?id=${this.stickerId}`
        await this.$store.dispatch('DELETE_REQUEST', { url })
        this.closeModal()
        this.deleting = false
      } catch (error) {
        console.log(error)
        this.$Message.error('Не удалось выполнить удаление')
        this.deleting = false
      }
    },
    closeModal () {
      this.stickerId = ''
      this.stickerName = ''
      this.stickerDescription = ''
      this.stickerDateTime = moment().format()
      this.stickerColor = ''
      this.stickerPerformer = ''
      this.editModal = false
      this.notificationTime = '00:30:00'
    },
    openModal (sticker) {
      const {
        id,
        name,
        description,
        end_time,
        color,
        assigned_to_id,
        end_time_notify_lag
      } = sticker

      this.stickerId = id
      this.stickerName = name
      this.stickerDescription = description
      this.stickerDateTime = moment(end_time).format('DD.MM.YYYY HH:mm')
      this.stickerColor = color
      this.stickerPerformer = assigned_to_id
      this.notificationTime = end_time_notify_lag
      this.editModal = true
    },
    hexToRgbA,
    getFullName
  }
}
</script>

<style lang="less">
.stickers {
  // width: 515px;

  &__add-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__filter {
    display: flex;
    align-items: center;

    .ms-text {
      margin-left: 8px;
    }
  }

  &__container {
    margin-top: 8px;
  }

  &__item {
    display: flex;
    padding: 4px 8px;
    position: relative;
    box-sizing: border-box;
    border-bottom: 1px solid rgba(232, 234, 236, 0.5);
    align-items: center;

    &:first-child {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }

    &:last-child {
      border-bottom: 1px solid transparent;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    &-checkbox {
      .ivu-checkbox-inner {
        border-radius: 50%;
      }
    }

    &-name {
      cursor: pointer;
      flex: 1;
      margin-right: 8px;

      span {
        word-break: break-all;
      }
    }

    &-subinfo {
      margin-left: auto;
      flex-shrink: 0;
      text-align: right;
    }

    &-performer {
      margin-right: 8px;
    }
  }

  &__modal {
    &-row {
      &.dates {
        display: flex;

        .date-cell {
          flex: 1;

          &:last-child {
            margin-left: 16px;
          }
        }

        .ivu-select,
        .ivu-date-picker {
          width: 100%;
        }
      }

      &:not(:first-child) {
        margin-top: 8px;
      }

      .ms-help {
        font-size: 11px;
      }

      &.name {
        display: flex;

        .ivu-color-picker {
          margin-left: 8px;
        }
      }

      .ivu-select,
      .ivu-date-picker {
        width: calc(50% - 8px);
      }
    }

    &-controls {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>
